import { getLogger } from '@logtape/logtape';

import { Room, RoomType } from '@/models/Room.ts';
import { ColumnType, KanbanColumn } from '@/models/RoomView.ts';
import { SelectionOption } from '@/models/SelectionOption.ts';
import { Appointment, AppointmentBookStatus, DailyAppointment } from '@/models/appointment/Appointment.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';
import { CheckinStatus } from '@/models/call-system/models/CallSystemUser.ts';
import { DCTaskStatus } from '@/models/doc-cirrus/DCTask.ts';

import { getRoomId, isCheckedIn, isCheckedOut, isCheckinArrived, isCheckinPending } from '@/utils/appointmentUtils.ts';

const logger = getLogger(['util', 'room-view-column']);

const base_columns: Map<string, KanbanColumn<ColumnType, DailyAppointment>> = new Map([
    [
        'expected',
        {
            id: 'expected',
            collapsed: false,
            title: 'ERWARTET',
            type: ColumnType.EXPECTED,
            items: [],
        },
    ],
    [
        'discharged',
        {
            id: 'discharged',
            collapsed: false,
            title: 'ENTLASSEN',
            type: ColumnType.DISCHARGED,
            items: [],
        },
    ],
]);

// TODO: add unit test
const columnTypeFromString = (type: RoomType): ColumnType | undefined => {
    const type_map: Record<string, ColumnType> = {
        [RoomType.ROOM]: ColumnType.TREATMENT_ROOM,
        [RoomType.WAITING_ROOM]: ColumnType.WAITING_ROOM,
    };

    const column_type = type_map[type];
    if (!column_type) {
        // TODO: managed unrecognized type properly
        logger.warn('Unrecognized room type {type}', { type: type });
    }

    return column_type;
};

export const populateColumnsWithAppointments = (
    appointments: DailyAppointment[],
    columns: Map<string, KanbanColumn<ColumnType, DailyAppointment>>,
    getCheckinData: (appointment: Appointment) => UserAppointmentDto | undefined
): Map<string, KanbanColumn<ColumnType, DailyAppointment>> | undefined => {
    const newColumnsMap = new Map(columns);

    // Retrieve expected and discharged columns
    const expectedColumn = newColumnsMap.get(ColumnType.EXPECTED);
    const dischargedColumn = newColumnsMap.get(ColumnType.DISCHARGED);
    const waitingRoomColumn = [...newColumnsMap.values()].find((column) => column.type === ColumnType.WAITING_ROOM);

    if (!expectedColumn || !dischargedColumn) {
        // Error in the code
        logger.error('Error while getting expected or discharged column');
        return undefined;
    }

    // Clear existing appointments
    for (const key of newColumnsMap.keys()) {
        const column = newColumnsMap.get(key);
        if (!column) continue;
        column.items = [];
    }

    // Populate appointments to columns
    for (const apt of appointments) {
        const checkinData = getCheckinData(apt);

        if (
            isCheckedOut(checkinData?.appointment?.checkout_status) ||
            apt.status === AppointmentBookStatus.CANCELLED ||
            apt.status === AppointmentBookStatus.DECLINED ||
            (apt.status === AppointmentBookStatus.COMPLETED && !getRoomId(apt)) ||
            !!apt.is_no_show
        ) {
            dischargedColumn.items.push(apt);
            continue;
        }

        // edge case, if the room_id is not present for some reason
        const checkinStatus = checkinData?.appointment?.checkin_status;
        if (
            (isCheckinArrived(checkinStatus) || isCheckedIn(checkinStatus)) &&
            !isCheckedOut(checkinData?.appointment?.checkout_status) &&
            !getRoomId(apt) &&
            waitingRoomColumn
        ) {
            logger.debug('Moving appointment with no roomId to waiting room');
            waitingRoomColumn.items.push(apt);
            continue;
        }

        const columnId = getRoomId(apt) || ColumnType.EXPECTED;
        const column = newColumnsMap.get(columnId);

        if (!column) {
            logger.warn('No column found for room {roomId} / column {columnId}', {
                roomId: getRoomId(apt),
                columnId: columnId,
            });
            continue;
        }

        column.items.push(apt);
        newColumnsMap.set(columnId, column);
    }

    newColumnsMap.set(ColumnType.EXPECTED, expectedColumn);
    newColumnsMap.set(ColumnType.DISCHARGED, dischargedColumn);

    return newColumnsMap;
};

// TODO: add unit test
export const generateColumns = (rooms: Room[] = []): Map<string, KanbanColumn<ColumnType, DailyAppointment>> => {
    const columns = new Map(base_columns);

    for (const room of rooms) {
        const column_type = columnTypeFromString(room.room_type);

        if (!column_type) continue;

        columns.set(room.room_id, {
            collapsed: false,
            id: room.room_id,
            title: room.room_name,
            type: column_type,
            items: [],
            instance_id: room.instance_id,
        });
    }

    return columns;
};

export const getRoomsSelection = (rooms: Room[], checkin_status?: CheckinStatus): SelectionOption[] => {
    let resultRooms = sortRooms(rooms);
    if (isCheckinPending(checkin_status)) {
        resultRooms = resultRooms.filter((room) => room.room_type === RoomType.WAITING_ROOM);
    }
    return resultRooms.map((room) => ({
        label: room.room_name,
        value: room.room_id,
    }));
};

export const resolveSelectedRoom = (
    explicitSelection: string | undefined,
    roomsSelection: SelectionOption[],
    isSelectionDisabled: boolean
): string => {
    if (explicitSelection) return explicitSelection;
    if (isSelectionDisabled) return '';
    return roomsSelection.length === 1 ? roomsSelection[0].value : '';
};

// Sort all in alphabetic order and have all waiting rooms on the top
const sortRooms = (rooms: Room[]): Room[] => {
    const waiting_rooms = rooms
        .filter((room) => room.room_type === RoomType.WAITING_ROOM)
        .sort((a, b) => a.room_name.localeCompare(b.room_name));

    const treatment_rooms = rooms
        .filter((room) => room.room_type !== RoomType.WAITING_ROOM)
        .sort((a, b) => a.room_name.localeCompare(b.room_name));

    return [...waiting_rooms, ...treatment_rooms];
};

export const applyOptimisticMove = <T extends ColumnType | DCTaskStatus, K extends { id: string }>(
    columns: Map<string, KanbanColumn<T, K>>,
    itemId: string,
    destinationColumnId: string
): Map<string, KanbanColumn<T, K>> => {
    const destination = columns.get(destinationColumnId);
    if (!destination) return columns;

    if (destination.items.some((item) => item.id === itemId)) return columns;

    let moved: K | undefined;
    for (const column of columns.values()) {
        moved = column.items.find((item) => item.id === itemId);
        if (moved) break;
    }

    if (!moved) return columns;

    const patched = new Map(columns);
    for (const [id, column] of patched) {
        if (column.items.some((item) => item.id === itemId)) {
            patched.set(id, { ...column, items: column.items.filter((item) => item.id !== itemId) });
        }
    }
    patched.set(destinationColumnId, { ...destination, items: [...destination.items, moved] });
    return patched;
};
